import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import { altioraToast, toastApiError } from "@/components/altiora/toast";
import {
  PageShell, PageHeader, Card, Section, Pill, Badge, PrimaryButton,
  GhostButton, EmptyState, SkeletonList, SkeletonCard,
  NAVY, CHAMPAGNE, SLATE,
} from "@/components/altiora/PremiumUI";
import { Users, ArrowLeft, ExternalLink, BookOpen, MapPin, Briefcase,
             CheckCircle2, LogOut } from "lucide-react";

/**
 * Communities — Network OS · communautés métier / territoriales
 *
 * Design-System-native (Phase B) :
 *   · PageShell + PageHeader
 *   · Grid Cards with hover-lift micro-interaction
 *   · SkeletonCard grid on initial load
 *   · Detail view with membership toggle + curated AI resources
 *   · Unified toasts, no ad-hoc styles
 *   · Fully responsive (1/2/3 column grid at sm/md/lg)
 */
export default function Communities() {
  const [list, setList]                   = useState([]);
  const [selected, setSelected]           = useState(null);
  const [detail, setDetail]               = useState(null);
  const [loading, setLoading]             = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [joining, setJoining]             = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/network/communities");
      setList(data.items || []);
    } catch (e) { toastApiError(e, "Chargement impossible"); }
    finally { setLoading(false); }
  }, []);

  const loadDetail = useCallback(async (cid) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/network/communities/${cid}`);
      setDetail(data);
    } catch (e) { toastApiError(e, "Détails indisponibles"); }
    finally { setDetailLoading(false); }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    if (selected) loadDetail(selected); else setDetail(null);
  }, [selected, loadDetail]);

  const toggleMembership = async () => {
    if (!detail) return;
    setJoining(true);
    try {
      if (detail.is_member) {
        await api.post(`/network/communities/${detail.id}/leave`);
        altioraToast.success("Vous avez quitté la communauté");
      } else {
        await api.post(`/network/communities/${detail.id}/join`);
        altioraToast.success("Bienvenue dans la communauté");
      }
      await Promise.all([loadDetail(detail.id), loadList()]);
    } catch (e) { toastApiError(e, "Action échouée"); }
    finally { setJoining(false); }
  };

  return (
    <PageShell testid="communities-page">
      <TopBar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        {!selected ? (
          <>
            <PageHeader
              eyebrow="ALTIORA · Network OS"
              title="Communautés"
              subtitle="Rassemblements ciblés autour d'un métier, un secteur, un territoire ou un objectif. Chaque communauté est enrichie automatiquement par les ressources UKE™ pertinentes." />

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                {[0, 1, 2, 3, 4, 5].map(i => <SkeletonCard key={i} testid={`community-skeleton-${i}`} />)}
              </div>
            ) : list.length === 0 ? (
              <Card className="p-4 mt-4">
                <EmptyState icon={Users}
                               title="Aucune communauté active"
                               description="Les nouvelles communautés apparaîtront ici dès leur ouverture."
                               testid="communities-empty" />
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4"
                      data-testid="communities-grid">
                {list.map((c) => <CommunityTile key={c.id} c={c} onOpen={() => setSelected(c.id)} />)}
              </div>
            )}
          </>
        ) : (
          <>
            <GhostButton onClick={() => setSelected(null)}
                             testid="community-back-btn">
              <ArrowLeft size={12} aria-hidden="true" />
              Retour aux communautés
            </GhostButton>

            {detailLoading || !detail ? (
              <div className="mt-4"><SkeletonList rows={4} /></div>
            ) : (
              <div className="mt-4 space-y-4" data-testid="community-detail">
                <Card className="p-6">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.28em]"
                              style={{ color: CHAMPAGNE }}>
                        Communauté
                      </div>
                      <h2 className="mt-2 font-serif text-2xl tracking-tight"
                             style={{ color: NAVY }}>
                        {detail.name_fr}
                      </h2>
                      <p className="mt-3 text-[13px] leading-relaxed" style={{ color: SLATE }}>
                        {detail.description_fr}
                      </p>
                    </div>
                    {detail.is_member ? (
                      <GhostButton onClick={toggleMembership} disabled={joining}
                                        testid="community-toggle-membership">
                        {joining ? "…" : <><LogOut size={12} aria-hidden="true" /> Quitter</>}
                      </GhostButton>
                    ) : (
                      <PrimaryButton onClick={toggleMembership} disabled={joining}
                                            testid="community-toggle-membership">
                        {joining ? "…" : <><CheckCircle2 size={12} aria-hidden="true" /> Rejoindre</>}
                      </PrimaryButton>
                    )}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
                    <Pill><Users size={10} /> {detail.members_count || 0} membres</Pill>
                    {detail.sectors?.map(s => <Pill key={s} tone="gold"><Briefcase size={10} /> {s}</Pill>)}
                    {detail.territories?.map(t => <Pill key={t}><MapPin size={10} /> {t}</Pill>)}
                  </div>
                </Card>

                {detail.curated_resources?.length > 0 && (
                  <Card className="p-6" testid="community-resources">
                    <Section icon={BookOpen}
                                title="Ressources recommandées par l'IA"
                                hint="Sélection APKE™ mise à jour automatiquement.">
                      <div className="mt-3 space-y-2">
                        {detail.curated_resources.map(r => (
                          <div key={r.id}
                                  className="rounded-lg p-3"
                                  style={{ border: `1px solid ${CHAMPAGNE}22`, background: "#fff" }}>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="navy" size="sm">{r.kind}</Badge>
                              <span className="text-[13px] font-semibold" style={{ color: NAVY }}>
                                {r.title}
                              </span>
                            </div>
                            {r.summary_fr && (
                              <div className="text-[12px] leading-relaxed" style={{ color: SLATE }}>
                                {r.summary_fr.slice(0, 240)}
                              </div>
                            )}
                            {r.url && (
                              <a href={r.url} target="_blank" rel="noreferrer"
                                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest transition hover:opacity-80"
                                  style={{ color: CHAMPAGNE }}>
                                <ExternalLink size={10} /> Ouvrir
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </Section>
                  </Card>
                )}

                {detail.members_preview?.length > 0 && (
                  <Card className="p-6" testid="community-members-preview">
                    <Section icon={Users} title="Aperçu des membres">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                        {detail.members_preview.map(m => (
                          <div key={m.id}
                                  className="rounded-lg p-3"
                                  style={{ border: `1px solid ${CHAMPAGNE}22`, background: "#fff" }}>
                            <div className="text-[13px] font-semibold" style={{ color: NAVY }}>
                              {m.display_name}
                            </div>
                            {m.headline_fr && (
                              <div className="text-[11px] mt-0.5" style={{ color: SLATE }}>
                                {m.headline_fr}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </Section>
                  </Card>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </PageShell>
  );
}

// ── Card tile with hover lift (consistent with AdaptiveHome CardTile) ─
function CommunityTile({ c, onOpen }) {
  return (
    <button
      onClick={onOpen}
      data-testid={`community-card-${c.id}`}
      className="group text-left rounded-xl p-4 transition-all bg-white
                    hover:-translate-y-0.5 hover:shadow-lg focus-visible:-translate-y-0.5"
      style={{ border: `1px solid ${CHAMPAGNE}33` }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="h-8 w-8 rounded-lg flex items-center justify-center"
                 style={{ background: `${CHAMPAGNE}22`, color: NAVY }}>
          <Users size={14} aria-hidden="true" />
        </div>
        {c.is_member && <Badge variant="green" size="sm">Membre</Badge>}
      </div>
      <div className="font-serif text-[16px] leading-snug" style={{ color: NAVY }}>
        {c.name_fr}
      </div>
      <div className="mt-1 text-[12px] leading-relaxed line-clamp-3" style={{ color: SLATE }}>
        {c.description_fr}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] font-mono"
              style={{ color: SLATE }}>
        <span className="inline-flex items-center gap-1"><Users size={10} /> {c.members_count || 0}</span>
        {c.sectors?.slice(0, 1).map(s => (
          <span key={s} className="inline-flex items-center gap-1"><Briefcase size={10} /> {s}</span>
        ))}
        {c.territories?.slice(0, 1).map(t => (
          <span key={t} className="inline-flex items-center gap-1"><MapPin size={10} /> {t}</span>
        ))}
      </div>
    </button>
  );
}
